import { Link } from 'react-router-dom';
import Cookies from 'js-cookie';

interface VideoSection {
  title: string;
  subtitle?: string;
  description: string;
  bulletPoints?: string[];
  bulletIntro?: string;
  closingText?: string;
  videoUrl: string;
  showCta?: boolean;
}

const sections: VideoSection[] = [
  {
    title: "Start Here. Activate Your Membership to Begin",
    subtitle: "Welcome",
    description: "You requested more information, and now you're moments away from unlocking everything inside your account. In the next minute you can activate your membership and step into a platform designed to help you save money, earn money, and participate in a growing digital ecosystem.",
    bulletIntro: "When you join, you unlock:",
    bulletPoints: [
      "Massive travel and shopping discounts",
      "A simple income opportunity anyone can start",
      "Access to the FFLOW ecosystem",
      "Eligibility for optional node ownership",
      "Benefits built to support long term financial growth"
    ],
    videoUrl: "https://player.vimeo.com/video/1131127859?badge=0&autopause=0&player_id=0&app_id=58479",
  },
  {
    title: "Save 30 to 70 Percent Every Time You Travel or Shop",
    subtitle: "Savings Engine",
    description: "Your membership gives you access to wholesale pricing normally reserved for insiders. Hotels, flights, cruises, car rentals, entertainment, brand name shopping and more.",
    bulletIntro: "You receive:",
    bulletPoints: [
      "Real wholesale rates",
      "Savings that often exceed your monthly membership cost with a single booking",
      "Even deeper discounts when paying with FFLOW"
    ],
    closingText: "If you travel or shop even a few times per year, this platform can pay for itself many times over. This is the easiest money you will ever save.",
    videoUrl: "https://player.vimeo.com/video/1131127859?badge=0&autopause=0&player_id=0&app_id=58479",
  },
  {
    title: "Create Daily and Weekly Income by Sharing Real Value",
    subtitle: "Affiliate Income Opportunity",
    description: "Our affiliate program gives you a simple path to earn commissions without selling products or managing inventory. You share a membership that genuinely helps people save money. We handle the rest.",
    bulletIntro: "You unlock:",
    bulletPoints: [
      "Commissions every time someone uses your link",
      "Recurring monthly income as members stay active",
      "A global digital business you can run from your phone",
      "Earnings potential starting the moment you activate",
      "No experience required"
    ],
    closingText: "Most people already look for ways to save money. You earn by helping them do it.",
    videoUrl: "https://player.vimeo.com/video/1131127859?badge=0&autopause=0&player_id=0&app_id=58479",
    showCta: true,
  },
  {
    title: "Own a FFLOW Node and Earn Passive Daily Rewards",
    subtitle: "FFLOW and Node Ownership",
    description: "Node ownership is optional, but it is one of the most powerful opportunities inside the ecosystem.",
    bulletIntro: "Owning a FFLOW Node gives you:",
    bulletPoints: [
      "Daily FFLOW reward emissions",
      "A digital asset that requires no tech skills",
      "Participation in the foundation of our network",
      "Long term upside as the network expands",
      "Early adopter positioning when demand is increasing"
    ],
    closingText: "This is one of the easiest ways to participate in blockchain rewards without complexity.",
    videoUrl: "https://player.vimeo.com/video/1145429244?badge=0&autopause=0&player_id=0&app_id=58479",
  },
  {
    title: "Everything You Get for Only $20 Per Month",
    subtitle: "Membership Breakdown",
    description: "Your membership unlocks the entire platform and all income opportunities.",
    bulletIntro: "Included with your membership:",
    bulletPoints: [
      "Unlimited travel and shopping savings",
      "Access to the affiliate program",
      "Eligibility to own FFLOW Nodes",
      "Member only benefits",
      "One time $10 setup",
      "No contract",
      "Cancel anytime",
      "Instant activation"
    ],
    closingText: "For the cost of a lunch, you unlock a platform designed to help you save money and create new income streams.",
    videoUrl: "https://player.vimeo.com/video/1131127859?badge=0&autopause=0&player_id=0&app_id=58479",
  },
];

const MoreInfo = () => {
  const sponsor = Cookies.get('sponsor');
  const joinUrl = sponsor
    ? `https://joinclubtravel.com/${sponsor}`
    : 'https://joinclubtravel.com';

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-900/95 backdrop-blur-md border-b border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/">
            <img src="/main-logo-white.png" alt="Xcelerate" className="h-10 md:h-12" />
          </Link>
          <a
            href={joinUrl}
            className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-2 px-6 rounded-lg transition-all hover:scale-105"
          >
            Join Now
          </a>
        </div>
      </header>

      {/* Hero Section - Compact */}
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 py-10 md:py-12 px-4 border-b border-gray-800">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-white mb-3">
            Your Journey to <span className="text-orange-400">Financial Freedom</span> Starts Here
          </h1>
          <p className="text-base md:text-lg text-gray-400">
            You're here because you want more options and better opportunities. This is where that begins.
          </p>
        </div>
      </div>

      {/* Content Sections */}
      <div className="max-w-6xl mx-auto px-4 py-8 md:py-12">
        {sections.map((section, index) => (
          <div
            key={index}
            className={`relative grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 py-8 md:py-12 ${
              index !== sections.length - 1 ? 'border-b border-gray-800' : ''
            }`}
          >
            {/* Left: Section Number + Video (Compact) */}
            <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
              {/* Section Badge */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-orange-500 flex items-center justify-center text-white font-bold text-xs md:text-sm">
                  {index + 1}
                </div>
                {section.subtitle && (
                  <span className="text-orange-400 text-xs md:text-sm font-medium">
                    {section.subtitle}
                  </span>
                )}
              </div>

              {/* Compact Video */}
              <div className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700">
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                  <iframe
                    src={section.videoUrl}
                    frameBorder="0"
                    allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    title={section.title}
                  ></iframe>
                </div>
              </div>
            </div>

            {/* Right: Content (Takes 2 columns) */}
            <div className={`lg:col-span-2 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
              <h2 className="text-xl sm:text-2xl md:text-[1.7rem] font-bold text-white mb-3 md:mb-4 leading-tight">
                {section.title}
              </h2>

              <p className="text-sm md:text-base text-gray-400 leading-relaxed mb-5">
                {section.description}
              </p>

              {/* Bullet Points - Special layout for section 2, responsive grid for others */}
              {section.bulletPoints && (
                <div className="mb-5">
                  <p className="text-gray-500 text-xs uppercase tracking-wider font-semibold mb-3">
                    {section.bulletIntro}
                  </p>
                  {index === 1 ? (
                    /* Section 2: Two columns - bullets left, closing text right */
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        {section.bulletPoints.map((point, pointIndex) => (
                          <div key={pointIndex} className="flex items-start gap-2">
                            <svg className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-300 text-sm">{point}</span>
                          </div>
                        ))}
                      </div>
                      {section.closingText && (
                        <div className="flex items-center">
                          <p className="text-gray-400 italic border-l-2 border-orange-500 pl-4 text-sm">
                            {section.closingText}
                          </p>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* Other sections: Standard 2-column grid */
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {section.bulletPoints.map((point, pointIndex) => (
                        <div key={pointIndex} className="flex items-start gap-2">
                          <svg className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-300 text-sm">{point}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Closing Text - skip for section 2 since it's inline */}
              {section.closingText && index !== 1 && (
                <p className="text-gray-400 italic border-l-2 border-orange-500 pl-4 text-sm">
                  {section.closingText}
                </p>
              )}

              {/* CTA Button for section 3 */}
              {section.showCta && (
                <div className="mt-6">
                  <a
                    href={joinUrl}
                    className="inline-block bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-8 rounded-lg transition-all hover:scale-105"
                  >
                    Start Earning Today
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Final CTA Section */}
      <div className="bg-gradient-to-r from-orange-600 to-orange-500 py-12 md:py-16">
        <div className="max-w-4xl mx-auto text-center px-4">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-4">
            Activate Your Membership and Start Saving Today
          </h2>
          <p className="text-base md:text-lg text-white/90 mb-6">
            You're one step away from unlocking full access.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 md:p-6 mb-8 max-w-md mx-auto">
            <p className="text-white font-semibold mb-3 text-sm md:text-base">Activate now and receive:</p>
            <ul className="text-left space-y-2 text-white/90 text-sm">
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Insider travel and shopping pricing
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Your personal affiliate dashboard
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Commission earning ability immediately
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Eligibility for FFLOW Node ownership
              </li>
              <li className="flex items-center">
                <svg className="w-4 h-4 text-white mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Full member access in seconds
              </li>
            </ul>
          </div>

          <a
            href={joinUrl}
            className="inline-block bg-white text-orange-500 font-bold py-3 px-8 rounded-lg hover:bg-gray-100 transition-colors text-base md:text-lg"
          >
            Activate My Membership
          </a>

          <p className="text-white/80 text-xs md:text-sm mt-4">
            Activation takes less than one minute. Instant access. No contract.
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 py-6 md:py-8 border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-gray-500 text-xs md:text-sm">
            Check your email for more details. Questions? Contact us at support@joinclubtravel.com
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MoreInfo;
